import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Homepage Section 9 — Final CTA.
 * Dark panel: left copy + 3 role cards, right inline "Schedule a Demo" form.
 * The form submits directly to the submit-demo-request edge function
 * (DB row + Discord alert). No modal.
 */


type RoleCard = {
  label: string;
  labelColor: string;
  glow: string;
  border: string;
  panel: string;
  line1: string;
  line2: string;
  cta: string;
  btn: string;
};

const roleCards: RoleCard[] = [
  {
    label: "SHIPPERS",
    labelColor: "#12D6A6",
    glow: "0 0 34px -6px rgba(2,133,126,0.55)",
    border: "rgba(18,214,166,0.55)",
    panel: "linear-gradient(180deg,rgba(4,40,38,0.85) 0%,rgba(6,19,26,0.9) 100%)",
    line1: "Free to get started.",
    line2: "No credit card.",
    cta: "Sign Up Free",
    btn: "linear-gradient(180deg,#12BE95 0%,#02857E 100%)",
  },
  {
    label: "BROKERS",
    labelColor: "#3B92F6",
    glow: "0 0 34px -6px rgba(37,99,235,0.5)",
    border: "rgba(59,146,246,0.5)",
    panel: "linear-gradient(180deg,rgba(9,25,52,0.85) 0%,rgba(6,19,26,0.9) 100%)",
    line1: "Win direct",
    line2: "shipper freight.",
    cta: "Sign Up",
    btn: "linear-gradient(180deg,#3B8CF6 0%,#1D5FD8 100%)",
  },
  {
    label: "CARRIERS",
    labelColor: "#A56BF7",
    glow: "0 0 34px -6px rgba(139,92,246,0.5)",
    border: "rgba(165,107,247,0.5)",
    panel: "linear-gradient(180deg,rgba(28,15,52,0.85) 0%,rgba(6,19,26,0.9) 100%)",
    line1: "Find loads that",
    line2: "fit your lanes.",
    cta: "Sign Up",
    btn: "linear-gradient(180deg,#9B5CF6 0%,#7A34E0 100%)",
  },
];

const fieldBase =
  "w-full rounded-lg border bg-[#0A1A20]/80 px-4 py-3.5 text-[15px] text-white placeholder:text-[#7E9A98] outline-none transition focus:border-[#12BE95]/60";

type Fields = { name: string; email: string; company: string; phone: string; role: string; shipments: string };
type Errors = Partial<Record<keyof Fields, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

const validate = (f: Fields): Errors => {
  const e: Errors = {};
  if (!f.name.trim()) e.name = "Name is required";
  if (!f.email.trim()) e.email = "Work email is required";
  else if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email address";
  if (!f.company.trim()) e.company = "Company is required";
  if (!f.phone.trim()) e.phone = "Phone is required";
  else {
    const d = f.phone.replace(/\D/g, "");
    if (d.length < 10 || d.length > 15) e.phone = "Enter a valid phone number";
  }
  if (!f.role) e.role = "Please select one";
  return e;
};

const HomeFinalCTA: React.FC = () => {
  const [fields, setFields] = useState<Fields>({
    name: "",
    email: "",
    company: "",
    phone: "",
    role: "",
    shipments: "",
  });
  const [honeypot, setHoneypot] = useState("");
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const errors = validate(fields);
  const isValid = Object.keys(errors).length === 0;
  const err = (k: keyof Fields) => (touched[k] ? errors[k] : undefined);
  const cls = (k: keyof Fields) => `${fieldBase} ${err(k) ? "border-red-400/70" : "border-white/10"}`;

  const set =
    (k: keyof Fields) =>
    (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFields((p) => ({ ...p, [k]: ev.target.value }));
      setFormError(null);
    };
  const blur = (k: keyof Fields) => () => setTouched((p) => ({ ...p, [k]: true }));

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setTouched({ name: true, email: true, company: true, phone: true, role: true });
    if (!isValid || submitting) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const { data, error } = await supabase.functions.invoke("submit-demo-request", {
        body: {
          name: fields.name.trim(),
          company: fields.company.trim(),
          email: fields.email.trim(),
          phone: fields.phone.trim(),
          role: fields.role,
          shipments: fields.shipments,
          website: honeypot,
          source: "homepage_final_cta",
        },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Submission failed");
      setDone(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      setFormError(
        /429|too many/i.test(msg)
          ? "Too many requests from this connection. Please try again later."
          : "We couldn't submit your request. Please try again, or email support@lanerunner.com.",
      );
    } finally {
      setSubmitting(false);
    }
  };


  return (
  <section className="bg-[#F7FAFC] py-14 sm:py-20">
    <div className="mx-auto w-full max-w-[1540px] px-5 sm:px-8">
      <div
        className="relative overflow-hidden rounded-[22px] px-6 py-12 shadow-[0_44px_100px_-45px_rgba(6,19,26,0.65)] sm:px-12 sm:py-16 lg:px-16 lg:py-20"
        style={{ background: "linear-gradient(150deg,#06171C 0%,#041217 45%,#051B1F 100%)" }}
      >
        {/* teal corner glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 top-1/4 h-[420px] w-[420px] rounded-full blur-[110px]"
          style={{ background: "radial-gradient(circle, rgba(2,133,126,0.45) 0%, rgba(2,133,126,0) 70%)" }}
        />

        <div className="relative grid grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,54%)_minmax(0,46%)] lg:gap-16">
          {/* LEFT */}
          <div>
            <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#12D6A6]">
              Get started fast
            </p>
            <h2 className="mt-4 text-[38px] font-semibold leading-[1.12] tracking-[-0.02em] text-white sm:text-[52px]">
              Start moving freight
              <br />
              in minutes.
            </h2>
            <p className="mt-6 max-w-[36ch] text-[16.5px] leading-[1.65] text-[#B4C6C4]">
              No long onboarding. No implementation calls. No contracts.
              Sign up, post your first load, and start comparing quotes today.
            </p>

            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {roleCards.map((c) => (
                <div
                  key={c.label}
                  className="rounded-xl border px-5 py-6 text-center"
                  style={{ borderColor: c.border, background: c.panel, boxShadow: c.glow }}
                >
                  <p
                    className="text-[17px] font-bold tracking-[0.01em]"
                    style={{ color: c.labelColor }}
                  >
                    {c.label}
                  </p>
                  <p className="mt-4 text-[14.5px] leading-[1.5] text-[#C7D6D4]">
                    {c.line1}
                    <br />
                    {c.line2}
                  </p>
                  <div className="mx-auto my-5 h-px w-[85%] bg-white/12" />
                  <Link
                    to="/pricing"
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-3 text-[14.5px] font-semibold text-white transition hover:brightness-110"
                    style={{ background: c.btn }}
                  >
                    {c.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — demo form (submits straight to Discord + DB) */}
          <div
            className="rounded-2xl border border-white/10 px-6 py-8 sm:px-9 sm:py-10"
            style={{ background: "linear-gradient(180deg,rgba(8,26,30,0.75) 0%,rgba(5,17,21,0.75) 100%)" }}
          >
            {done ? (
              <div className="py-8 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#12D6A6]" />
                <h3 className="mt-5 text-[26px] font-semibold text-white">Request received</h3>
                <p className="mx-auto mt-3 max-w-[34ch] text-[15.5px] leading-[1.6] text-[#B4C6C4]">
                  Thanks — we'll be in touch shortly to schedule your demo.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h3 className="text-[30px] font-semibold leading-tight tracking-[-0.01em] text-white sm:text-[34px]">
                  Talk to Sales
                </h3>
                <p className="mt-3 text-[15.5px] leading-[1.6] text-[#B4C6C4]">
                  For enterprise shippers, larger teams, and strategic partnerships.
                </p>

                <div className="mt-7 space-y-3.5">
                  <div>
                    <input
                      className={cls("name")}
                      placeholder="Name"
                      aria-label="Name"
                      value={fields.name}
                      onChange={set("name")}
                      onBlur={blur("name")}
                    />
                    {err("name") && <p className="mt-1.5 text-[13px] text-red-300">{err("name")}</p>}
                  </div>

                  <div>
                    <input
                      type="email"
                      className={cls("email")}
                      placeholder="Work Email"
                      aria-label="Work Email"
                      value={fields.email}
                      onChange={set("email")}
                      onBlur={blur("email")}
                    />
                    {err("email") && <p className="mt-1.5 text-[13px] text-red-300">{err("email")}</p>}
                  </div>

                  <div>
                    <input
                      className={cls("company")}
                      placeholder="Company"
                      aria-label="Company"
                      value={fields.company}
                      onChange={set("company")}
                      onBlur={blur("company")}
                    />
                    {err("company") && <p className="mt-1.5 text-[13px] text-red-300">{err("company")}</p>}
                  </div>

                  <div>
                    <input
                      type="tel"
                      className={cls("phone")}
                      placeholder="Phone"
                      aria-label="Phone"
                      value={fields.phone}
                      onChange={set("phone")}
                      onBlur={blur("phone")}
                    />
                    {err("phone") && <p className="mt-1.5 text-[13px] text-red-300">{err("phone")}</p>}
                  </div>

                  {/* honeypot — hidden from humans */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    className="pointer-events-none absolute h-0 w-0 opacity-0"
                  />

                  <div className="pt-2">
                    <label htmlFor="demo-role" className="block text-[14.5px] text-[#C7D6D4]">
                      I am a
                    </label>
                    <select
                      id="demo-role"
                      value={fields.role}
                      onChange={set("role")}
                      onBlur={blur("role")}
                      className={`${cls("role")} mt-2 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23A9BFBD%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:18px_18px] bg-[right_14px_center] bg-no-repeat pr-11`}
                    >
                      <option value="">Select one</option>
                      <option value="Shipper">Shipper</option>
                      <option value="Broker">Broker</option>
                      <option value="Carrier">Carrier</option>
                    </select>
                    {err("role") && <p className="mt-1.5 text-[13px] text-red-300">{err("role")}</p>}
                  </div>

                  <div className="pt-2">
                    <label htmlFor="demo-shipments" className="block text-[14.5px] text-[#C7D6D4]">
                      Approx. shipments per month
                    </label>
                    <select
                      id="demo-shipments"
                      value={fields.shipments}
                      onChange={set("shipments")}
                      className={`${cls("shipments")} mt-2 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23A9BFBD%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:18px_18px] bg-[right_14px_center] bg-no-repeat pr-11`}
                    >
                      <option value="">Select one</option>
                      <option value="1-9">1–9</option>
                      <option value="10-49">10–49</option>
                      <option value="50-99">50–99</option>
                      <option value="100-499">100–499</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                </div>

                {formError && (
                  <p
                    role="alert"
                    className="mt-5 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-3 text-[14px] text-red-200"
                  >
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-4 text-[15.5px] font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100"
                  style={{ background: "linear-gradient(180deg,#12BE95 0%,#02857E 100%)" }}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                  <>
                      Talk to Sales <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>

                <p className="mt-5 text-center text-[14.5px] text-[#9FB6B3]">
                  Looking for a product walkthrough? Watch the{" "}
                  <a
                    href="https://www.youtube.com/@LaneRunner-USA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#12D6A6] hover:underline"
                  >
                    LaneRunner demo videos
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  </section>

  );
};

export default HomeFinalCTA;
